import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Star, MapPin, TrendingUp, Sparkles, Truck, ShieldCheck,
  Shirt, Cpu, Home as HomeIcon, Printer, Hammer, Gift, Briefcase, Tag,
  Image as ImageIcon, FileText, Award, Flag
} from "lucide-react";
import Layout from "@/components/Layout";
import ProductCardCompact from "@/components/ProductCardCompact";
import Carousel from "@/components/Carousel";
import SafeImage from "@/components/SafeImage";
import { api } from "@/lib/api";

const CATEGORY_CARDS = [
  { slug: "apparel", name: "Apparel", icon: Shirt, hint: "Tees · Hoodies · Denim",
    img: "https://images.unsplash.com/photo-1469334031218-e382a71b716b?auto=format&fit=crop&w=700&q=70",
    accent: "#702A32" },
  { slug: "electronics", name: "Electronics", icon: Cpu, hint: "Audio · Smart home",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=700&q=70",
    accent: "#2A4B3A" },
  { slug: "home-decor", name: "Home & Living", icon: HomeIcon, hint: "Ceramics · Wood",
    img: "https://images.pexels.com/photos/7167000/pexels-photo-7167000.jpeg?auto=compress&w=700",
    accent: "#C78B1B" },
  { slug: "printing", name: "Print on Demand", icon: Printer, hint: "Upload · Print · Ship",
    img: "https://images.unsplash.com/photo-1611250282006-4484dd3fba6b?auto=format&fit=crop&w=700&q=70",
    accent: "#702A32" },
  { slug: "office-supplies", name: "Business Printing", icon: Briefcase, hint: "Cards · Banners",
    img: "https://images.unsplash.com/photo-1607344645866-009c320c5ab8?auto=format&fit=crop&w=700&q=70",
    accent: "#1A1A18" },
  { slug: "handmade", name: "Handmade", icon: Hammer, hint: "From Canadian studios",
    img: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=700&q=70",
    accent: "#2A4B3A" },
  { slug: "gifts", name: "Gifts", icon: Gift, hint: "Curated picks",
    img: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=700&q=70",
    accent: "#C78B1B" },
  { slug: "accessories", name: "Office & Accessories", icon: Tag, hint: "Everyday essentials",
    img: "https://images.unsplash.com/photo-1531346878377-a5be20888e57?auto=format&fit=crop&w=700&q=70",
    accent: "#702A32" },
];

const PRINT_SERVICES = [
  { name: "Custom Shirts", img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=600&q=70", tag: "POD" },
  { name: "Custom Hoodies", img: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?auto=format&fit=crop&w=600&q=70", tag: "POD" },
  { name: "Business Cards", img: "https://images.unsplash.com/photo-1607344645866-009c320c5ab8?auto=format&fit=crop&w=600&q=70", tag: "POD" },
  { name: "Roll-Up Banners", img: "https://images.unsplash.com/photo-1605379399642-870262d3d051?auto=format&fit=crop&w=600&q=70", tag: "POD" },
  { name: "Vinyl Stickers", img: "https://images.unsplash.com/photo-1611250282006-4484dd3fba6b?auto=format&fit=crop&w=600&q=70", tag: "POD" },
  { name: "Custom Mugs", img: "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?auto=format&fit=crop&w=600&q=70", tag: "POD" },
];

export default function Home() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/products/featured").then(({ data }) => setData(data)).catch(() => {});
    api.get("/products/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const heroProducts = useMemo(() => (data?.trending?.slice(0, 6) || []), [data]);

  return (
    <Layout>
      {/* ============ HERO ============ */}
      <section className="border-b border-[#E5E3DD] bg-[#FDFBF7]" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-12 gap-6 py-6 md:py-10 items-center">
          <div className="md:col-span-5">
            <div className="overline mb-3">A Canadian Marketplace · Made local</div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#1A1A18]">
              Canada's marketplace for <em className="not-italic text-[#702A32]">creators</em>, <em className="not-italic text-[#2A4B3A]">printers</em> & local brands.
            </h1>
            <p className="text-sm md:text-base text-[#5C5A56] font-light mt-4 leading-relaxed">
              Browse thousands of products from independent Canadian shops — from heritage crafts to print-on-demand.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/discover" className="btn-primary inline-flex items-center gap-2" data-testid="hero-shop-btn">
                Shop the marketplace <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link to="/register" className="btn-secondary" data-testid="hero-open-store-btn">Open your store</Link>
            </div>

            {/* Trust strip */}
            <div className="grid grid-cols-4 gap-2 mt-6 pt-5 border-t border-[#E5E3DD]" data-testid="hero-trust-stats">
              <TrustStat value={fmtK(stats?.products)} label="Products" />
              <TrustStat value={fmtK(stats?.stores)} label="Stores" />
              <TrustStat value={fmtK(stats?.printers)} label="Printers" />
              <TrustStat value={`${stats?.provinces || 13}`} label="Provinces" sub="& Territories" />
            </div>
          </div>

          {/* RIGHT: 3x2 product collage with featured spotlight */}
          <div className="md:col-span-7">
            <div className="grid grid-cols-3 gap-2 md:gap-3" data-testid="hero-product-collage">
              {heroProducts.map((p, i) => (
                <motion.div
                  key={p.product_id}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.04, duration: 0.4 }}
                  className={i === 0 ? "row-span-2 col-span-2" : ""}
                >
                  <Link to={`/product/${p.slug}`} className="group relative block surface overflow-hidden h-full">
                    <div className={`overflow-hidden bg-[#F3F2F0] ${i === 0 ? "aspect-[16/13]" : "aspect-square"}`}>
                      <SafeImage src={p.featured_image} alt={p.name} className="product-card-img w-full h-full object-cover" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white">
                      <div className={`font-medium leading-tight line-clamp-1 ${i === 0 ? "text-sm md:text-base" : "text-xs"}`}>{p.name}</div>
                      <div className={`font-mono ${i === 0 ? "text-base md:text-lg" : "text-xs"}`}>${p.price.toFixed(2)}</div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick benefits ribbon */}
        <div className="border-t border-[#E5E3DD] bg-[#F3F2F0]">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex flex-wrap gap-x-8 gap-y-1 text-xs text-[#5C5A56]">
            <Benefit icon={Truck} text="Shipped across Canada" />
            <Benefit icon={ShieldCheck} text="Verified Canadian merchants" />
            <Benefit icon={Award} text={`${stats?.reviews?.toLocaleString() || "1,000"}+ verified reviews`} />
            <Benefit icon={Flag} text="Local makers · Local jobs" />
            <Benefit icon={Sparkles} text="Print-on-demand in 3-5 days" />
          </div>
        </div>
      </section>

      {/* ============ TRENDING CAROUSEL ============ */}
      <Row title="Trending right now" badge={<span className="tag-pill bg-[#F2E8E9] text-[#702A32]"><TrendingUp className="w-3 h-3 inline mr-1" strokeWidth={1.5} />Hot</span>} link="/discover?sort=trending" testid="row-trending">
        <Carousel testid="carousel-trending">
          {data?.trending?.map((p) => <ProductCardCompact key={p.product_id} product={p} />)}
        </Carousel>
      </Row>

      {/* ============ FEATURED STORES (compact card row) ============ */}
      <Row title="Featured Canadian stores" subtitle="Independent shops worth following" link="/stores" testid="row-stores">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {data?.stores?.slice(0, 6).map((s) => (
            <Link key={s.merchant_id} to={`/store/${s.slug}`} className="group surface overflow-hidden hover:shadow-md transition-shadow" data-testid={`store-mini-${s.merchant_id}`}>
              <div className="aspect-[16/10] overflow-hidden bg-[#F3F2F0]">
                <SafeImage src={s.banner} alt={s.name} className="product-card-img w-full h-full object-cover" />
              </div>
              <div className="p-3 flex items-center gap-2">
                {s.logo && <SafeImage src={s.logo} className="w-9 h-9 rounded-full object-cover border border-[#E5E3DD] shrink-0" alt="" />}
                <div className="min-w-0">
                  <div className="font-heading text-base leading-tight line-clamp-1">{s.name}</div>
                  <div className="text-[11px] text-[#8A8782] flex items-center gap-1.5">
                    <Star className="w-3 h-3 fill-[#C78B1B] text-[#C78B1B]" strokeWidth={1.5} />
                    {s.rating?.toFixed(1)} · {s.follower_count.toLocaleString()}+ fans
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Row>

      {/* ============ SHOP BY CATEGORY ============ */}
      <Row title="Shop by category" subtitle="Eight ways into the marketplace" testid="row-categories">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CATEGORY_CARDS.map((c) => (
            <Link key={c.slug} to={`/discover?category=cat_${c.slug.split("-")[0]}`} className="group relative overflow-hidden surface aspect-[5/4]" data-testid={`category-card-${c.slug}`}>
              <SafeImage src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white">
                <c.icon className="w-5 h-5 mb-1.5" strokeWidth={1.5} style={{ color: c.accent === "#1A1A18" ? "#fff" : c.accent }} />
                <h3 className="font-heading text-lg md:text-xl leading-tight">{c.name}</h3>
                <p className="text-[11px] md:text-xs text-white/75 mt-0.5">{c.hint}</p>
              </div>
            </Link>
          ))}
        </div>
      </Row>

      {/* ============ FEATURED PRINT SERVICES (POD focused) ============ */}
      <section className="bg-[#1A1A18] text-[#FDFBF7] py-10 md:py-14" data-testid="row-print-services">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-[#8A8782] mb-1">Upload · Print · Ship</div>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight">Featured print services</h2>
            </div>
            <Link to="/discover?type=POD" className="text-sm text-[#C78B1B] hover:underline inline-flex items-center gap-1">
              Browse all POD <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {PRINT_SERVICES.map((s) => (
              <Link key={s.name} to="/discover?type=POD" className="group block bg-[#2a2a26] border border-[#3a3a36] overflow-hidden hover:border-[#702A32] transition-colors" data-testid={`print-service-${s.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="aspect-square overflow-hidden">
                  <SafeImage src={s.img} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-2.5 flex items-center justify-between">
                  <span className="text-sm font-medium">{s.name}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-[#C78B1B] opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-6 grid md:grid-cols-3 gap-3 text-xs text-[#8A8782]">
            <PodPerk icon={ImageIcon} title="Live mockup preview" text="Drag, resize, rotate your design before checkout." />
            <PodPerk icon={Printer} title="3–5 day production" text="Trusted Canadian printers ship direct from their studio." />
            <PodPerk icon={FileText} title="PNG · JPG · SVG · PDF" text="Up to 20MB artwork. Low-res warnings included." />
          </div>
        </div>
      </section>

      {/* ============ NEW ARRIVALS ============ */}
      <Row title="New arrivals" subtitle="Fresh from Canadian stores" link="/discover?sort=latest" testid="row-new">
        <Carousel testid="carousel-new">
          {data?.latest?.map((p) => <ProductCardCompact key={p.product_id} product={p} />)}
        </Carousel>
      </Row>

      {/* ============ BEST SELLERS ============ */}
      <Row title="Best sellers" subtitle="Most-purchased on MapleMarket this month" link="/discover?sort=trending" testid="row-best">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {data?.bestsellers?.slice(0, 12).map((p, i) => (
            <div key={p.product_id} className="relative">
              <span className="absolute z-10 top-2 left-2 w-6 h-6 bg-[#1A1A18] text-white text-xs font-mono flex items-center justify-center">{i + 1}</span>
              <ProductCardCompact product={p} />
            </div>
          ))}
        </div>
      </Row>

      {/* ============ LOCAL CANADIAN MAKERS (Creators spotlight) ============ */}
      <section className="border-t border-[#E5E3DD] bg-[#FDFBF7] py-10 md:py-14" data-testid="row-makers">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline mb-1">Made in Canada</div>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight">Local Canadian makers</h2>
            </div>
            <Link to="/stores" className="text-sm text-[#702A32] hover:underline inline-flex items-center gap-1">All makers <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {(data?.creators?.length ? data.creators : data?.stores?.slice(0, 4) || []).map((m) => (
              <Link key={m.merchant_id} to={`/store/${m.slug}`} className="relative aspect-[4/5] group overflow-hidden surface" data-testid={`maker-card-${m.merchant_id}`}>
                <SafeImage src={m.banner} alt={m.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <span className="tag-pill bg-white/15 backdrop-blur text-white border border-white/20">{m.types?.[0]}</span>
                  <h3 className="font-heading text-2xl mt-2 leading-tight">{m.name}</h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-white/80">
                    <MapPin className="w-3 h-3" strokeWidth={1.5} /> {m.location}
                    <span>·</span>
                    <Star className="w-3 h-3 fill-[#C78B1B] text-[#C78B1B]" strokeWidth={1.5} /> {m.rating?.toFixed(1)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ RECENTLY OPENED STORES ============ */}
      <Row title="Recently opened stores" subtitle="New on the marketplace this season" link="/stores" testid="row-recent-stores">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data?.recently_opened?.map((s) => (
            <Link key={s.merchant_id} to={`/store/${s.slug}`} className="surface p-4 hover:shadow-md transition-shadow flex gap-3" data-testid={`recent-store-${s.merchant_id}`}>
              {s.logo && <SafeImage src={s.logo} alt="" className="w-14 h-14 rounded-full object-cover border border-[#E5E3DD] shrink-0" />}
              <div className="min-w-0">
                <div className="font-heading text-base leading-tight line-clamp-1">{s.name}</div>
                <div className="text-xs text-[#8A8782] mt-0.5 line-clamp-1">{s.location}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {s.types?.slice(0, 2).map((t) => <span key={t} className="tag-pill bg-[#EFECE6] text-[#5C5A56]">{t}</span>)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Row>

      {/* ============ TOP RATED (compact strip) ============ */}
      <Row title="Top rated this season" subtitle="Loved by customers, verified by reviews" link="/discover?sort=top-rated" testid="row-toprated">
        <Carousel testid="carousel-toprated">
          {data?.top_rated?.map((p) => <ProductCardCompact key={p.product_id} product={p} />)}
        </Carousel>
      </Row>

      {/* ============ WHY SELL ON MAPLEMARKET ============ */}
      <section className="bg-[#1A1A18] text-[#FDFBF7]" data-testid="home-cta-banner">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="overline text-[#8A8782] mb-2">Why sell on MapleMarket</div>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight leading-tight">
              Your storefront, your customers, your terms.
            </h2>
            <p className="text-[#a8a6a1] font-light mt-4 max-w-xl text-sm md:text-base">
              Get a beautiful storefront at <span className="font-mono text-[#FDFBF7]">/store/your-name</span>. Sell standard goods, run print-on-demand, or accept custom requests. Connect your own domain when you're ready.
            </p>
            <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6 mt-6 text-sm text-[#FDFBF7]">
              <Bullet>Beautiful merchant-branded storefront</Bullet>
              <Bullet>Print-on-demand tooling built in</Bullet>
              <Bullet>Multi-merchant orders supported</Bullet>
              <Bullet>Built-in reviews, ratings & follows</Bullet>
              <Bullet>Stripe payouts (Interac coming)</Bullet>
              <Bullet>Custom domain (coming soon)</Bullet>
            </ul>
            <div className="flex gap-3 mt-7">
              <Link to="/register" className="btn-primary" data-testid="cta-open-store">Open a store</Link>
              <Link to="/login" className="btn-secondary !text-white !border-white/30 hover:!bg-white/10">Merchant sign in</Link>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3">
            <Plan label="Commission" value="From 6%" desc="Pay only when you sell" />
            <Plan label="Subscription" value="$29–$49" desc="Flat monthly" />
            <Plan label="Markup mode" value="From 0%" desc="Add to customer price" />
            <Plan label="Hybrid" value="Flex" desc="Sub + commission" />
          </div>
        </div>
      </section>
    </Layout>
  );
}

/* ---------- helpers ---------- */
function fmtK(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K+`;
  return `${n}+`;
}

function TrustStat({ value, label, sub }) {
  return (
    <div>
      <div className="font-heading text-2xl md:text-3xl leading-none text-[#1A1A18]">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#8A8782] mt-1">{label}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-[#8A8782]">{sub}</div>}
    </div>
  );
}

function Benefit({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-[#702A32]" strokeWidth={1.5} /> {text}
    </span>
  );
}

function Row({ title, subtitle, badge, link, children, testid }) {
  return (
    <section className="py-8 md:py-10 border-b border-[#E5E3DD] last:border-0" data-testid={testid}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between gap-3 mb-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              {subtitle && <span className="overline">{subtitle}</span>}
              {badge}
            </div>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight">{title}</h2>
          </div>
          {link && (
            <Link to={link} className="text-sm text-[#702A32] hover:underline inline-flex items-center gap-1 shrink-0">
              See all <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}

function PodPerk({ icon: Icon, title, text }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-[#2a2a26] border border-[#3a3a36]">
      <Icon className="w-5 h-5 text-[#C78B1B] shrink-0 mt-0.5" strokeWidth={1.5} />
      <div>
        <div className="text-[#FDFBF7] text-sm font-medium">{title}</div>
        <div className="text-[#8A8782] text-xs leading-relaxed">{text}</div>
      </div>
    </div>
  );
}

function Plan({ label, value, desc }) {
  return (
    <div className="border border-[#3a3a36] p-4">
      <div className="text-[#8A8782] text-[10px] uppercase tracking-widest">{label}</div>
      <div className="font-heading text-2xl mt-1">{value}</div>
      <div className="text-xs text-[#a8a6a1] mt-0.5">{desc}</div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="w-1 h-1 rounded-full bg-[#C78B1B] mt-2 shrink-0" />
      <span className="text-sm text-[#a8a6a1]">{children}</span>
    </li>
  );
}
